import { FC } from 'react';
import {
  ZoomOutMap as ResetZoomIcon,
} from '@mui/icons-material';
import { FormControl, MenuItem, SelectChangeEvent, Tooltip, Typography } from '@mui/material';
import { ZoomControlContainer, ZoomIconButton, ZoomSelect } from './ZoomControl.styles';
import { useZoom, ZOOM_CONFIG } from '../../context/ZoomContext';

interface ZoomControlProps {
  /** Whether to show the zoom control */
  visible?: boolean;
  /** Custom className for styling */
  className?: string;
}

const ZoomControl: FC<ZoomControlProps> = ({ visible = true, className }) => {
  const { zoomLevel, setZoomLevel, resetZoom } = useZoom();

  if (!visible) {
    return null;
  }

  const handleDropdownChange = (event: SelectChangeEvent<unknown>) => {
    setZoomLevel(event.target.value as number);
  };

  const isDefaultZoom = zoomLevel === ZOOM_CONFIG.DEFAULT;

  // Generate zoom level options with proper precision
  const zoomOptions = [];
  const steps = Math.round((ZOOM_CONFIG.MAX - ZOOM_CONFIG.MIN) / ZOOM_CONFIG.STEP);
  for (let i = 0; i <= steps; i++) {
    const level = ZOOM_CONFIG.MIN + (i * ZOOM_CONFIG.STEP);
    zoomOptions.push(Math.round(level * 100) / 100);
  }

  return (
    <ZoomControlContainer className={className} elevation={4}>
      {/* Zoom Label */}
      <Tooltip
        sx={{
          '& .MuiTooltip-popper': {
            backgroundColor: 'white',
            color: 'black',
          },
        }}
        title={
          <>
            <Typography variant="caption" color="white">
            This setting will change the zoom level of the application body.<br/>
            </Typography>
            <Typography variant="caption" color="white">
            The default zoom level is 80%, you can reset it to this level by clicking the reset button to the right.<br/>
            </Typography>
            <Typography variant="caption" color="white">
            You can also zoom in and out using the mouse wheel.<br/>
            </Typography>
          </>}
      >
        <Typography variant="caption" color="text.secondary">
        Application body zoom:
        </Typography>
      </Tooltip>
      {/* Zoom Level Dropdown */}
        
      <FormControl size="small">
        <ZoomSelect
          value={zoomLevel}
          onChange={handleDropdownChange}
          aria-label="Zoom level"
        >
          {zoomOptions.map(level => (
            <MenuItem key={level} value={level}>
              {Math.round(level * 100)}%
            </MenuItem>
          ))}
        </ZoomSelect>
      </FormControl>

      {/* Reset Zoom Button */}
      <span>
        <ZoomIconButton
          onClick={resetZoom}
          disabled={isDefaultZoom}
          size="small"
          color="primary"
        >
          <ResetZoomIcon />
        </ZoomIconButton>
      </span>
    </ZoomControlContainer>
  );
};

export default ZoomControl;
